import { contentManagementService } from './contentManagementService';

export interface EmailMessage {
  to: string | string[];
  subject: string;
  html: string;
  text?: string;
}

export interface EmailResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

class EmailService {
  /**
   * Send email using Supabase Edge Function (with Resend configured in dashboard)
   */
  async sendEmail(message: EmailMessage): Promise<EmailResponse> {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: Array.isArray(message.to) ? message.to.join(', ') : message.to,
          subject: message.subject,
          htmlContent: message.html,
          textContent: message.text
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        return {
          success: true,
          messageId: result.messageId
        };
      } else {
        console.error('Email sending failed:', result);
        return {
          success: false,
          error: result.error || 'Failed to send email'
        };
      }
    } catch (error) {
      console.error('Email service error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Email sending failed'
      };
    }
  }

  /**
   * Send order confirmation email using template
   */
  async sendOrderConfirmationEmail(
    email: string,
    customerName: string,
    orderNumber: string,
    total: number,
    orderDetails: string
  ): Promise<EmailResponse> {
    try {
      const trackingUrl = `${window.location.origin}/orders/${orderNumber}`;

      const emailContent = await contentManagementService.processEmailTemplate('order_confirmation_email', {
        customerName,
        orderNumber,
        total: total.toFixed(2),
        orderDetails,
        trackingUrl
      });

      if (!emailContent) {
        // Fallback email
        return this.sendEmail({
          to: email,
          subject: `Order #${orderNumber} Confirmed - Dough Man Foods`,
          html: this.generateOrderConfirmationHTML(customerName, orderNumber, total, orderDetails, trackingUrl),
          text: this.generateOrderConfirmationText(customerName, orderNumber, total, orderDetails, trackingUrl)
        });
      }

      return this.sendEmail({
        to: email,
        subject: emailContent.subject,
        html: emailContent.html,
        text: emailContent.text || undefined
      });
    } catch (error) {
      console.error('Error sending order confirmation email:', error);
      // Fallback email
      const trackingUrl = `${window.location.origin}/orders/${orderNumber}`;
      return this.sendEmail({
        to: email,
        subject: `Order #${orderNumber} Confirmed - Dough Man Foods`,
        html: this.generateOrderConfirmationHTML(customerName, orderNumber, total, orderDetails, trackingUrl),
        text: this.generateOrderConfirmationText(customerName, orderNumber, total, orderDetails, trackingUrl)
      });
    }
  }

  /**
   * Send welcome email using template
   * Currently uses built-in HTML template (database template system disabled)
   */
  async sendWelcomeEmail(
    email: string,
    customerName: string
  ): Promise<EmailResponse> {
    const websiteUrl = window.location.origin;

    // Use built-in HTML template directly (skip database template lookup for now)
    return this.sendEmail({
      to: email,
      subject: `Welcome to Dough Man Foods, ${customerName}!`,
      html: this.generateWelcomeHTML(customerName, websiteUrl),
      text: this.generateWelcomeText(customerName, websiteUrl)
    });
  }

  /**
   * Send order status update email
   */
  async sendOrderStatusUpdateEmail(
    email: string,
    customerName: string,
    orderNumber: string,
    status: string,
    message?: string
  ): Promise<EmailResponse> {
    const statusDisplayNames: Record<string, string> = {
      'confirmed': 'Confirmed',
      'preparing': 'Being Prepared',
      'ready': 'Ready for Pickup/Delivery',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };

    const statusName = statusDisplayNames[status] || status;
    const trackingUrl = `${window.location.origin}/orders/${orderNumber}`;

    return this.sendEmail({
      to: email,
      subject: `Order #${orderNumber} Update - ${statusName}`,
      html: this.generateStatusUpdateHTML(customerName, orderNumber, statusName, message, trackingUrl),
      text: this.generateStatusUpdateText(customerName, orderNumber, statusName, message, trackingUrl)
    });
  }

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(
    email: string,
    customerName: string,
    resetUrl: string
  ): Promise<EmailResponse> {
    return this.sendEmail({
      to: email,
      subject: 'Reset Your Password - Dough Man Foods',
      html: this.generatePasswordResetHTML(customerName, resetUrl),
      text: this.generatePasswordResetText(customerName, resetUrl)
    });
  }

  /**
   * Send promotional email with Ghana cultural context
   */
  async sendPromotionalEmail(
    email: string,
    customerName: string,
    promoTitle: string,
    promoContent: string,
    ctaUrl?: string
  ): Promise<EmailResponse> {
    return this.sendEmail({
      to: email,
      subject: `${promoTitle} - Dough Man Foods`,
      html: this.generatePromotionalHTML(customerName, promoTitle, promoContent, ctaUrl),
      text: this.generatePromotionalText(customerName, promoTitle, promoContent, ctaUrl)
    });
  }

  // Fallback HTML generators
  private generateOrderConfirmationHTML(
    customerName: string,
    orderNumber: string,
    total: number,
    orderDetails: string,
    trackingUrl: string
  ): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .header { background: #f8f9fa; padding: 20px; text-align: center; }
          .content { padding: 20px; }
          .button { background: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block; }
          .footer { background: #f8f9fa; padding: 20px; text-align: center; font-size: 14px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🍩 Dough Man Foods</h1>
          <h2>Order Confirmation</h2>
        </div>
        <div class="content">
          <h3>Hi ${customerName}! 🎉</h3>
          <p>Your order #${orderNumber} has been confirmed and we're preparing your celebration packages!</p>
          
          <div style="background: #f8f9fa; padding: 15px; border-radius: 4px; margin: 20px 0;">
            <h4>Order Details:</h4>
            <p><strong>Order Number:</strong> ${orderNumber}</p>
            <p><strong>Total:</strong> GH₵${total.toFixed(2)}</p>
            <div>${orderDetails}</div>
          </div>
          
          <p>
            <a href="${trackingUrl}" class="button">Track Your Order</a>
          </p>
          
          <p>We'll send you updates as your order progresses. Thank you for choosing Dough Man Foods to celebrate your achievements!</p>
          
          <p style="font-style: italic; color: #666;">
            🇬🇭 Chaley, you force! We dey prepare your order with love from Accra.
          </p>
        </div>
        <div class="footer">
          <p>Dough Man Foods - Creating Moments That Matter</p>
          <p>Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329</p>
        </div>
      </body>
      </html>
    `;
  }

  private generateOrderConfirmationText(
    customerName: string,
    orderNumber: string,
    total: number,
    orderDetails: string,
    trackingUrl: string
  ): string {
    return `
Hi ${customerName}! 🎉

Your order #${orderNumber} has been confirmed!

Order Details:
- Order Number: ${orderNumber}
- Total: GH₵${total.toFixed(2)}
${orderDetails}

Track your order: ${trackingUrl}

We'll send you updates as your order progresses. Thank you for choosing Dough Man Foods!

🇬🇭 Chaley, you force! We dey prepare your order with love from Accra.

---
Dough Man Foods - Creating Moments That Matter
Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329
    `;
  }

  private generateWelcomeHTML(customerName: string, websiteUrl: string): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .header { background: linear-gradient(135deg, #ff6b35, #f7931e); padding: 30px; text-align: center; color: white; }
          .content { padding: 30px; }
          .button { background: #007bff; color: white; padding: 15px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; }
          .feature { display: inline-block; width: 30%; margin: 10px; padding: 20px; text-align: center; background: #f8f9fa; border-radius: 8px; }
          .footer { background: #2c3e50; padding: 20px; text-align: center; color: white; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🍩 Welcome to Dough Man Foods!</h1>
          <h2>Chaley ${customerName}, you force! 🎉</h2>
        </div>
        <div class="content">
          <p>Welcome to Ghana's premier celebration package platform!</p>
          
          <p>We are here to help you celebrate every win, big or small. From work promotions to personal milestones - we help young professionals in Accra celebrate their success with style!</p>
          
          <div style="text-align: center; margin: 30px 0;">
            <div class="feature">
              <h3>🎯 For Teams</h3>
              <p>Corporate celebration packages for workplace achievements</p>
            </div>
            <div class="feature">
              <h3>🏆 For You</h3>
              <p>Personal milestone celebration packages</p>
            </div>
            <div class="feature">
              <h3>👨‍🎓 For Students</h3>
              <p>Academic achievement celebration packages</p>
            </div>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${websiteUrl}/packages" class="button">Start Celebrating</a>
          </div>
          
          <p style="background: #fff3cd; padding: 15px; border-radius: 4px; border-left: 4px solid #ffc107;">
            <strong>🇬🇭 Ghana Cultural Touch:</strong><br>
            E bi so! We understand the Ghanaian way of celebrating success. Our packages are designed with authentic cultural elements that resonate with young professionals in Accra.
          </p>
        </div>
        <div class="footer">
          <p><strong>Dough Man Foods - Creating Moments That Matter</strong></p>
          <p>Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329 | Email: taste@doughmanfoods.com</p>
        </div>
      </body>
      </html>
    `;
  }

  private generateWelcomeText(customerName: string, websiteUrl: string): string {
    return `
Chaley ${customerName}, you force! 🎉

Welcome to Dough Man Foods - Ghana's premier celebration package platform!

We are here to help you celebrate every win, big or small. From work promotions to personal milestones - we help young professionals in Accra celebrate their success with style!

What we offer:
🎯 Corporate celebration packages for workplace achievements
🏆 Personal milestone celebration packages  
👨‍🎓 Academic achievement celebration packages

🇬🇭 Ghana Cultural Touch:
E bi so! We understand the Ghanaian way of celebrating success. Our packages are designed with authentic cultural elements that resonate with young professionals in Accra.

Start exploring: ${websiteUrl}/packages

---
Dough Man Foods - Creating Moments That Matter
Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329
    `;
  }

  private generateStatusUpdateHTML(
    customerName: string,
    orderNumber: string,
    status: string,
    message: string | undefined,
    trackingUrl: string
  ): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .header { background: #f8f9fa; padding: 20px; text-align: center; }
          .content { padding: 20px; }
          .status { background: #e3f2fd; padding: 15px; border-radius: 4px; margin: 20px 0; text-align: center; }
          .button { background: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block; }
          .footer { background: #f8f9fa; padding: 20px; text-align: center; font-size: 14px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🍩 Dough Man Foods</h1>
          <h2>Order Update</h2>
        </div>
        <div class="content">
          <h3>Hi ${customerName}!</h3>
          
          <div class="status">
            <h3>Order #${orderNumber}</h3>
            <h2 style="color: #1976d2;">${status}</h2>
            ${message ? `<p>${message}</p>` : ''}
          </div>
          
          <p>
            <a href="${trackingUrl}" class="button">View Order Details</a>
          </p>
          
          <p>Thank you for choosing Dough Man Foods for your celebration!</p>
        </div>
        <div class="footer">
          <p>Dough Man Foods - Creating Moments That Matter</p>
          <p>Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329</p>
        </div>
      </body>
      </html>
    `;
  }

  private generateStatusUpdateText(
    customerName: string,
    orderNumber: string,
    status: string,
    message: string | undefined,
    trackingUrl: string
  ): string {
    return `
Hi ${customerName}!

Order Update for #${orderNumber}:
Status: ${status}
${message ? `\n${message}` : ''}

View order details: ${trackingUrl}

Thank you for choosing Dough Man Foods!

---
Dough Man Foods - Creating Moments That Matter
Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329
    `;
  }

  private generatePasswordResetHTML(customerName: string, resetUrl: string): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .header { background: #f8f9fa; padding: 20px; text-align: center; }
          .content { padding: 20px; }
          .button { background: #dc3545; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block; }
          .warning { background: #fff3cd; padding: 15px; border-radius: 4px; border-left: 4px solid #ffc107; margin: 20px 0; }
          .footer { background: #f8f9fa; padding: 20px; text-align: center; font-size: 14px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🍩 Dough Man Foods</h1>
          <h2>Password Reset</h2>
        </div>
        <div class="content">
          <h3>Hi ${customerName}!</h3>
          <p>We received a request to reset your password for your Dough Man Foods account.</p>
          
          <p>
            <a href="${resetUrl}" class="button">Reset Password</a>
          </p>
          
          <div class="warning">
            <strong>Security Note:</strong> This link will expire in 24 hours. If you didn't request this reset, please ignore this email.
          </div>
          
          <p>If you have any questions, contact our support team.</p>
        </div>
        <div class="footer">
          <p>Dough Man Foods - Creating Moments That Matter</p>
          <p>Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329</p>
        </div>
      </body>
      </html>
    `;
  }

  private generatePasswordResetText(customerName: string, resetUrl: string): string {
    return `
Hi ${customerName}!

We received a request to reset your password for your Dough Man Foods account.

Reset your password: ${resetUrl}

Security Note: This link will expire in 24 hours. If you didn't request this reset, please ignore this email.

If you have any questions, contact our support team.

---
Dough Man Foods - Creating Moments That Matter
Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329
    `;
  }

  private generatePromotionalHTML(
    customerName: string,
    promoTitle: string,
    promoContent: string,
    ctaUrl?: string
  ): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .header { background: linear-gradient(135deg, #ff6b35, #f7931e); padding: 30px; text-align: center; color: white; }
          .content { padding: 30px; }
          .button { background: #28a745; color: white; padding: 15px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; }
          .promo { background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .footer { background: #2c3e50; padding: 20px; text-align: center; color: white; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🍩 Dough Man Foods</h1>
          <h2>${promoTitle}</h2>
        </div>
        <div class="content">
          <h3>Chaley ${customerName}! 🎉</h3>
          
          <div class="promo">
            ${promoContent}
          </div>
          
          ${ctaUrl ? `<div style="text-align: center; margin: 30px 0;"><a href="${ctaUrl}" class="button">Celebrate Now</a></div>` : ''}
          
          <p style="font-style: italic; color: #666;">
            🇬🇭 You force! Don't miss this opportunity to celebrate in style.
          </p>
        </div>
        <div class="footer">
          <p><strong>Dough Man Foods - Creating Moments That Matter</strong></p>
          <p>Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329</p>
        </div>
      </body>
      </html>
    `;
  }

  private generatePromotionalText(
    customerName: string,
    promoTitle: string,
    promoContent: string,
    ctaUrl?: string
  ): string {
    return `
${promoTitle}

Chaley ${customerName}! 🎉

${promoContent}

${ctaUrl ? `Celebrate now: ${ctaUrl}` : ''}

🇬🇭 You force! Don't miss this opportunity to celebrate in style.

---
Dough Man Foods - Creating Moments That Matter
Accra, Ghana | Phone: +233 201 100 533 / +233 274 010 329
    `;
  }
}

// Create service instance (no config needed - uses Supabase Edge Function with Resend)
export const emailService = new EmailService();